import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useInternalLinking from '@/hooks/useInternalLinking';

interface InternalLinkingEngineProps {
  enabled?: boolean;
  autoInjectLinks?: boolean;
  targetSelector?: string;
}

/**
 * Moteur de maillage interne automatique
 * Injecte automatiquement des liens internes pertinents dans le contenu
 */
const InternalLinkingEngine: React.FC<InternalLinkingEngineProps> = ({
  enabled = true,
  autoInjectLinks = true,
  targetSelector = '[data-auto-linking]'
}) => {
  const location = useLocation();
  const { generateAutomaticLinking, contextualLinks, isLoading } = useInternalLinking();
  const processedElements = useRef(new Set<Element>());

  useEffect(() => {
    if (!enabled || !autoInjectLinks || isLoading) return;

    const processElements = () => {
      const elements = document.querySelectorAll(targetSelector);
      
      elements.forEach(element => {
        // Éviter de traiter le même élément plusieurs fois
        if (processedElements.current.has(element)) return;
        
        const originalContent = element.innerHTML;
        const automaticLinks = generateAutomaticLinking(element.textContent || '');
        
        if (automaticLinks.length > 0) {
          let newContent = originalContent;
          
          // Injecter les liens de manière intelligente
          automaticLinks.forEach(link => {
            const regex = new RegExp(`\\b${link.keyword}\\b`, 'gi');
            const replacement = `<a href="${link.url}" class="internal-link text-primary hover:text-primary-600 underline decoration-primary/30 hover:decoration-primary transition-colors" data-auto-link="${link.keyword}">${link.anchor}</a>`;
            
            // S'assurer qu'on ne remplace pas du contenu déjà dans un lien
            if (!newContent.includes(`data-auto-link="${link.keyword}"`)) {
              newContent = newContent.replace(regex, replacement);
            }
          });
          
          if (newContent !== originalContent) {
            element.innerHTML = newContent;
            processedElements.current.add(element);
          }
        }
      });
    };

    // Traiter les éléments après un court délai pour s'assurer que le DOM est prêt
    const timeoutId = setTimeout(processElements, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [enabled, autoInjectLinks, targetSelector, generateAutomaticLinking, isLoading, location.pathname]);

  // Nettoyer les références lors du changement de page
  useEffect(() => {
    processedElements.current.clear();
  }, [location.pathname]);

  // Composant invisible - ne rend rien visuellement
  return null;
};

/**
 * Composant pour afficher les suggestions de liens manuelles
 */
export const InternalLinkingSuggestions: React.FC = () => {
  const { suggestions, contextualLinks, relatedPages, isLoading } = useInternalLinking();

  if (isLoading || (!suggestions.length && !contextualLinks.length && !relatedPages.length)) {
    return null;
  }

  return (
    <div className="internal-linking-suggestions bg-background/50 backdrop-blur-sm border border-border rounded-lg p-4 mt-8">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        Liens internes suggérés
      </h3>

      {contextualLinks.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-2">Liens contextuels</h4>
          <div className="grid gap-2">
            {contextualLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                className="text-primary hover:text-primary-600 underline decoration-primary/30 hover:decoration-primary transition-colors text-sm"
              >
                {link.anchor}
              </a>
            ))}
          </div>
        </div>
      )}

      {relatedPages.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-2">Pages connexes</h4>
          <div className="grid gap-2">
            {relatedPages.slice(0, 5).map((page, index) => (
              <a
                key={index}
                href={page.url}
                className="text-primary hover:text-primary-600 underline decoration-primary/30 hover:decoration-primary transition-colors text-sm flex items-center justify-between"
              >
                <span>{page.title}</span>
                <span className="text-xs text-muted-foreground">
                  {Math.round(page.relevance * 100)}% pertinent
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {suggestions.length > 0 && (
        <div>
          <h4 className="font-medium text-foreground mb-2">Opportunités de maillage</h4>
          <div className="grid gap-2">
            {suggestions.slice(0, 3).map((suggestion, index) => (
              <div key={index} className="text-sm text-muted-foreground">
                <span className="font-medium">{suggestion.keyword}</span> → 
                <a
                  href={suggestion.targetUrl}
                  className="text-primary hover:text-primary-600 underline decoration-primary/30 hover:decoration-primary transition-colors ml-1"
                >
                  {suggestion.suggestedAnchor}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalLinkingEngine;